/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { InputLabel, FormControl } from '@material-ui/core'
import { FormHelperText, Input } from '@mui/material'
// import { NumericFormat } from 'react-number-format'
import { NumericFormat as NumberFormat } from 'react-number-format'

import PropTypes from 'prop-types'
import classNames from 'classnames'

import generalInputStyles from '../styles'
import { defaultInputRules } from '../constants'

/**
 * Basic input component that uses Material-UI Input and React Hook Form Controller.
 * @param {Object} props - The props object.
 * @param {string} props.label - The label of the input.
 * @param {string} props.name - The name of the input. (Hook-Form related)
 * @param {boolean} [props.required=false] - Whether the input is required or not.
 * @param {string} [props.defaultValue=''] - The default value of the input.
 * @param {Object} [props.rules={}] - The validation rules for the input. Follows the same rules as React Hook Form.
 * @param {Object} [props.style={}] - The style object to be applied to the component.
 * @param {string} [props.containerClasses] - The classes to be applied to the container element.
 * @param {string} [props.labelClasses] - The classes to be applied to the label element.
 * @returns {JSX.Element} - The MoneyInput component.
 */
function MoneyInput({
  label,
  name,
  required = false,
  defaultValue = '',
  rules = {},
  style = {},
  containerClasses,
  showErrorMessage = true,
  valueClasses,
  labelClasses,
  ...rest
}) {
  const generalStyles = generalInputStyles()

  const inputRules = useMemo(() => {
    const formattedRules = Object.entries(rules).reduce((acc, [rule, value]) => {
      if (typeof value === 'object') {
        // No value is invalid
        if (!Object.values(value)?.length) return acc

        if (value?.message) return { ...acc, [rule]: value }
      }

      return { ...acc, [rule]: defaultInputRules[rule]({ value }) }
    }, {})

    if (required && !formattedRules.required) {
      return { ...formattedRules, required: defaultInputRules.required() }
    }

    return formattedRules
  }, [])

  const { errors, control, setValue } = useFormContext()

  useEffect(() => {
    setValue(name, defaultValue)
  }, [defaultValue, name, setValue])

  return (
    <FormControl
      style={{ ...style }}
      className={classNames(generalStyles.formControl, containerClasses ?? null)}
    >
      <InputLabel
        variant="standard"
        id={`${name}-label`}
        style={{ color: errors[name] ? '#C90000' : null, top: '-38px' }}
        className={classNames(generalStyles.inputLabel, labelClasses ?? null)}
      >
        {label}
        {required && <span className={generalStyles.required}>*</span>}
      </InputLabel>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={inputRules}
        as={
          <NumberFormat
            error={errors[name]}
            errorMessage={errors[name]?.message}
            customInput={Input}
            prefix="R$ "
            thousandSeparator="."
            decimalSeparator=","
            fixedDecimalScale
            allowNegative={false}
            allowLeadingZeros={false}
            fullWidth
            decimalScale={2}
            className={classNames(generalStyles.inputValue, valueClasses ?? null)}
            {...rest}
          />
        }
      />
      {showErrorMessage && (
        <FormHelperText className={generalStyles.errorMessage} error={!!errors[name]}>
          {errors[name]?.message}
        </FormHelperText>
      )}
    </FormControl>
  )
}

MoneyInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  defaultValue: PropTypes.string,
  rules: PropTypes.instanceOf(Object),
  style: PropTypes.instanceOf(Object),
  containerClasses: PropTypes.string,
  labelClasses: PropTypes.string,
}

MoneyInput.defaultProps = {
  required: false,
  showErrorMessage: true,
  defaultValue: '',
  rules: {},
  style: {},
  containerClasses: null,
  labelClasses: null,
}

export default MoneyInput
